import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { SeoHead } from '../components/SeoHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const ProdottiPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSiteSettings();
  const [searchParams] = useSearchParams();

  const validCats = ['tutti', 'freschi', 'specialita', 'conserve'];
  const catParam = searchParams.get('cat');
  const [activeCategory, setActiveCategory] = useState(
    validCats.includes(catParam) ? catParam : 'tutti'
  );

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = [
    { id: 'tutti', name: 'Tutti i Prodotti del Vesuvio' },
    { id: 'freschi', name: 'Prodotti Freschi — Pomodorini Napoletani' },
    { id: 'specialita', name: 'Specialità Locali Campane' },
    { id: 'conserve', name: 'Conserve Artigianali Vesuviane' }
  ];

  const filteredProducts = activeCategory === 'tutti' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const getCategoryLabel = (category) => {
    switch(category) {
      case 'freschi': return 'Prodotti Freschi';
      case 'specialita': return 'Specialità Locali';
      case 'conserve': return 'Conserve Artigianali';
      default: return category;
    }
  };

  return (
    <div data-testid="prodotti-page">
      <SeoHead
        title="Prodotti del Vesuvio | Pomodorino Napoletano, Conserve, Specialità Campane"
        description="Acquista i prodotti del Vesuvio: pomodorini napoletani freschi, conserve artigianali e specialità tipiche campane dall'azienda agricola Le Bontà del Vesuvio, Ercolano (NA). Coltivati nella terra del Piennolo DOP."
        keywords="pomodorino napoletano acquisto, prodotti del vesuvio online, conserve artigianali campane, pomodorino vesuviano, specialità locali campania, pomodoro del piennolo territorio, prodotti freschi vesuvio, ciliegino vesuviano"
        canonical="/prodotti"
      />

      {/* Hero */}
      <section className="relative flex items-center" data-testid="products-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/32873343/pexels-photo-32873343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Pomodorini napoletani del Vesuvio — prodotti tipici campani"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative container-custom pt-24 pb-6 md:pt-28 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent font-body text-xs uppercase tracking-widest mb-1 block">
              Dal Campo alla Tavola — Terra Vulcanica del Vesuvio
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white max-w-3xl">
              Prodotti del Vesuvio — Pomodorini Napoletani, Conserve e Specialità Campane
            </h1>
            <p className="font-body text-sm text-white/80 mt-2 max-w-xl">
              Pomodorini napoletani coltivati sulle pendici del Vesuvio, conserve artigianali e
              specialità tipiche campane. La stessa terra ricca di minerali vulcanici del Pomodoro
              del Piennolo DOP, metodi tradizionali, rispetto per la natura.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-spacing" data-testid="products-grid">
        <div className="container-custom">
          <Tabs defaultValue="tutti" className="w-full" onValueChange={setActiveCategory}>
            <TabsList className="w-full flex flex-wrap justify-center gap-2 mb-12 bg-transparent h-auto p-0">
              {categories.map((cat) => (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  data-testid={`tab-${cat.id}`}
                  className="px-6 py-3 rounded-full font-body text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-white border border-primary/20 data-[state=inactive]:bg-transparent data-[state=inactive]:text-text-DEFAULT hover:bg-primary/10 transition-colors"
                >
                  {cat.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map((cat) => (
              <TabsContent key={cat.id} value={cat.id} className="mt-0">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {[1, 2, 3].map((n) => (
                      <div key={n} className="animate-pulse">
                        <div className="aspect-[4/3] bg-background-paper rounded-xl mb-4" />
                        <div className="h-4 bg-background-paper rounded w-1/4 mb-2" />
                        <div className="h-6 bg-background-paper rounded w-3/4 mb-2" />
                        <div className="h-4 bg-background-paper rounded w-full" />
                      </div>
                    ))}
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="font-body text-text-muted text-lg">
                      Nessun prodotto trovato in questa categoria.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProducts.map((product, index) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="product-card group bg-white rounded-xl overflow-hidden border border-primary/10"
                        data-testid={`product-${product.id}`}
                      >
                        <div className="relative overflow-hidden bg-gray-100 aspect-square md:aspect-[4/5]">
                          {/* sfondo blur */}
                          <img
                            src={product.image_url}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover blur-xl scale-125 opacity-60 pointer-events-none"
                          />
                          {/* foto principale */}
                          <img
                            src={product.image_url}
                            alt={product.name}
                            loading="lazy"
                            className="relative z-10 w-full h-full object-contain"
                          />
                          {product.featured && (
                            <span className="absolute top-4 left-4 z-20 bg-secondary text-white text-xs font-body font-medium px-3 py-1 rounded-full">
                              In Evidenza
                            </span>
                          )}
                        </div>
                        <div className="p-6">
                          <span className="text-secondary text-xs uppercase tracking-widest font-body">
                            {getCategoryLabel(product.category)}
                          </span>
                          <h3 className="font-heading text-xl font-semibold text-text-DEFAULT mt-2 mb-3">
                            {product.name}
                          </h3>
                          <p className="font-body text-text-muted text-sm leading-relaxed mb-6">
                            {product.description}
                          </p>
                          <a
                            href={`https://wa.me/393382523925?text=Ciao!%20Vorrei%20informazioni%20su:%20${encodeURIComponent(product.name)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`product-whatsapp-${product.id}`}
                          >
                            <Button className="w-full bg-primary hover:bg-primary-dark text-white rounded-full py-5 font-body font-medium">
                              Richiedi Informazioni
                            </Button>
                          </a>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Info Section */}
      <section className="section-spacing bg-background-paper" data-testid="products-info">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-secondary font-body text-sm uppercase tracking-widest mb-4 block">
                {settings.prodotti_info_label}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-DEFAULT mb-6">
                {settings.prodotti_info_title}
              </h2>
              <div className="space-y-4 font-body text-text-muted leading-relaxed">
                <p>{settings.prodotti_info_p1}</p>
                <p>{settings.prodotti_info_p2}</p>
                <p>{settings.prodotti_info_p3}</p>
              </div>
              <div className="mt-8">
                <a
                  href="https://wa.me/393382523925?text=Ciao!%20Vorrei%20visitare%20la%20vostra%20azienda."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-primary hover:bg-primary-dark text-white rounded-full px-8 py-6 font-body font-medium btn-primary">
                    Prenota una Visita
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-4">
                <div className="aspect-square rounded-xl overflow-hidden">
                  <img
                    src={settings.prodotti_info_img1_url}
                    alt="Prodotto 1"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={settings.prodotti_info_img2_url}
                    alt="Prodotto 2"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={settings.prodotti_info_img3_url}
                    alt="Prodotto 3"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-xl overflow-hidden">
                  <img
                    src={settings.prodotti_info_img4_url}
                    alt="Prodotto 4"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
